import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  selectBalance,
  selectCashierConfiguration,
} from '@cashier/cashier-data-access';
import { FormField } from '@cashier/cashier-model';
import {
  eventBus,
  EventFactory,
  getCurrencySymbol,
} from '@cashier/cashier-shared';

import { useAmountValueContext } from '../../../contexts';
import { useFieldToucher } from '../../../hooks';
import { generateBonusPayload } from '../../bonus/helpers';
import { useBonusConfiguration } from '../../bonus/hooks';

interface UseAmountFieldProps {
  field: FormField;
}

interface UseAmountField {
  balance: string | null;
  onChange: (value: string, isFocused: boolean) => void;
}

export const useAmountField = ({
  field,
}: UseAmountFieldProps): UseAmountField => {
  const balance = useSelector(selectBalance);
  const { currency, version } = useSelector(selectCashierConfiguration);

  const { value: amount, setValue: setContextValue } = useAmountValueContext();

  const { touch } = useFieldToucher();

  //@TODO: move bonus logic to middleware
  const { getConfigurationByAmount } = useBonusConfiguration();

  useEffect(() => {
    const bonusConfiguration = getConfigurationByAmount(amount || '');
    const currencySymbol = (currency && getCurrencySymbol(currency)) || '';

    if (version === 'v3') {
      const bonusValue =
        amount && bonusConfiguration?.displayCalculator
          ? generateBonusPayload(
              amount,
              currencySymbol,
              bonusConfiguration.interval,
            )
          : null;

      eventBus.emit(
        EventFactory.bonusCalculated(
          `${currencySymbol}${amount || 0}`,
          bonusValue ? bonusValue.toUpperCase() : bonusValue,
        ),
      );
    }
  }, [version, amount, currency, getConfigurationByAmount]);

  const onChange = useCallback(
    (value: string, isFocused: boolean) => {
      setContextValue(value);

      touch({ ...field, value }, { interactionCompleted: !isFocused });
    },
    [field, setContextValue, touch],
  );

  return {
    balance,
    onChange,
  };
};
